import { ref, computed } from "vue";
import { useRoute } from "vue-router";

import { useCampaigns, useOrgCampaignDetail } from "@/queries/campaigns";
import { useAuthStore } from "@/stores/auth";
import { escapeHtml } from "@/utils/sanitizeHtml";

export interface ContextSelection {
  campaignId?: string;
  caseId?: string;
  caseName?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  step: string;
  type: "campaign" | "case" | "firm" | null;
}

export interface ContextStep {
  backStep?: string;
  choices: ContextChoice[];
  id: string;
  question: string;
  title: string;
}

export interface ContextChoice {
  action?: "select-campaign" | "select-case" | "start-conversation";
  contextType?: "campaign" | "case" | "firm";
  description?: string;
  id: string;
  nextStep?: string;
  text: string;
}

/**
 * Composable for managing the context selection flow.
 *
 * Phase B: previously gated chat depth on the HANDLING-org type. The chat
 * depth (firm-only vs case/campaign drill-in) is a function of whether the
 * user's org has its own underlying data to drill into — that's the same
 * concept as the dataSyncEnabled gate. Orgs without sync data only get
 * firm-level chat options.
 */
export function useContextSelection() {
  const route = useRoute();
  const authStore = useAuthStore();

  // Orgs without their own synced data only get firm-level chat.
  // Wrapped in computed so it re-evaluates if the auth state changes mid-session.
  const isNonHandlingUser = computed(() => !authStore.isDataSyncEnabled);

  // Reactive cache reads — vue-query keeps these fresh across the app.
  // currentOrgCampaignDetails reactively follows the current route's
  // (campaignId, raId) pair so case-context lookups always read the right data.
  const campaignsQuery = useCampaigns();
  const allCampaigns = computed(() => campaignsQuery.data.value ?? []);

  // Only read on a case page, to name the case: fetch just that case, and nothing elsewhere.
  const orgDetailCaseId = computed(() => route.params.caseId as string | undefined);
  const orgDetailCampaignId = computed(() =>
    orgDetailCaseId.value
      ? ((route.params.campaignId ?? route.params.id) as string | undefined)
      : undefined
  );
  const orgDetailRaId = computed(() => route.params.raId as string | undefined);
  const orgDetailQuery = useOrgCampaignDetail(orgDetailCampaignId, orgDetailRaId, orgDetailCaseId);
  const currentOrgCampaignDetails = computed(() => orgDetailQuery.data.value ?? null);

  // Current selection state
  const selection = ref<ContextSelection>({
    data: {},
    step: "initial",
    type: null,
  });

  // Context selection history for back navigation
  const stepHistory = ref<string[]>([]);

  // Check current page context to determine initial flow
  const currentPageContext = computed(() => {
    const path = route.path;

    // Check for case context first (most specific)
    // Case route: /campaigns/:campaignId/:raId/:caseId
    if (route.params.caseId && route.params.campaignId && route.params.raId) {
      // Find the campaign from the store synchronously
      const campaign = allCampaigns.value.find((c) => c.id === route.params.campaignId);
      return {
        campaign,
        campaignId: route.params.campaignId as string,
        id: route.params.caseId as string,
        type: "case" as const,
      };
    }

    // Check for campaign context
    // Campaign routes: /campaigns/:id (Overview) or /campaigns/:id/:raId (firm-scoped)
    if (path.includes("/campaigns/") && (route.params.id || route.params.campaignId)) {
      const campaignId = route.params.id || route.params.campaignId;
      // Find the campaign from the store synchronously
      const campaign = allCampaigns.value.find((c) => c.id === campaignId);
      return {
        campaign,
        id: campaignId as string,
        type: "campaign" as const,
      };
    }

    return {
      id: null,
      type: "firm" as const,
    };
  });

  // Define the decision tree steps based on your flowchart
  const steps = computed((): Record<string, ContextStep> => {
    const currentContext = currentPageContext.value;

    return {
      // Initial entry point - adapt based on current page
      initial: {
        choices: (() => {
          // For REFERRING users, only show firm option
          if (isNonHandlingUser.value) {
            return [
              {
                action: "start-conversation",
                contextType: "firm",
                description: "Ask about all campaigns you have access to",
                id: "firm",
                text: "Your Campaigns",
              },
            ];
          }

          // For HANDLING users, show all options based on context
          if (currentContext.type === "case") {
            return [
              {
                action: "start-conversation",
                contextType: "case",
                id: "yes-this-case",
                text: "Yes, this case",
              },
              {
                id: "different-case",
                nextStep: "case-same-campaign-check",
                text: "No, a different case",
              },
              {
                id: "different-campaign",
                nextStep: "campaign-picker-from-case",
                text: "No, a different campaign",
              },
              {
                action: "start-conversation",
                contextType: "firm",
                id: "about-firm",
                text: "No, it's about the firm",
              },
            ];
          } else if (currentContext.type === "campaign") {
            return [
              {
                action: "start-conversation",
                contextType: "campaign",
                id: "yes-this-campaign",
                text: "Yes, this campaign",
              },
              {
                id: "different-campaign",
                nextStep: "campaign-picker-from-campaign",
                text: "No, a different campaign",
              },
              {
                id: "specific-case",
                nextStep: "case-same-campaign-check-from-campaign",
                text: "No, a specific case",
              },
              {
                action: "start-conversation",
                contextType: "firm",
                id: "about-firm",
                text: "No, it's about the firm",
              },
            ];
          } else {
            return [
              {
                id: "specific-case",
                nextStep: "campaign-picker-for-case",
                text: "A specific case",
              },
              {
                id: "campaign",
                nextStep: "campaign-picker-for-campaign",
                text: "A campaign",
              },
              {
                action: "start-conversation",
                contextType: "firm",
                id: "firm",
                text: "The firm",
              },
            ];
          }
        })(),
        id: "initial",
        question: (() => {
          // For REFERRING users, show simplified question
          if (isNonHandlingUser.value) {
            return "What would you like to know about your campaigns?";
          }

          if (currentContext.type === "case") {
            // For case context - show specific case and campaign info
            const campaignName = currentContext.campaign?.name || "this campaign";

            // Try to get case data from the current store
            // We need to access the currentOrgCampaignDetails from the campaign store
            const currentCaseId = currentContext.id;
            const orgCampaignDetails = currentOrgCampaignDetails.value;

            let plaintiffName = "this case";
            let caseNumber = currentCaseId;

            if (orgCampaignDetails?.cases) {
              const currentCase = orgCampaignDetails.cases.find(
                // TODO: Fix this the next time the file is edited.
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (c: any) => c.id?.toString() === currentCaseId?.toString()
              );

              if (currentCase) {
                // Build plaintiff name from case data
                const nameParts = [];
                if (currentCase.firstName) nameParts.push(currentCase.firstName);
                if (currentCase.lastName) nameParts.push(currentCase.lastName);
                if (nameParts.length > 0) {
                  plaintiffName = nameParts.join(" ");
                }

                // Use case number if available, otherwise fall back to ID
                const caseNum = currentCase.caseNumber || currentCase.id;

                if (typeof caseNum === "string") {
                  caseNumber = caseNum;
                }
              }
            }

            return `You're currently on <strong>${escapeHtml(plaintiffName)}</strong>'s case (<strong>#${escapeHtml(String(caseNumber))}</strong>), in the <strong>${escapeHtml(campaignName)}</strong> campaign. Is this what your question is about?`;
          } else if (currentContext.type === "campaign") {
            const currentCampaignName = currentContext.campaign?.name || "this campaign";
            return `You're currently viewing the <strong>${escapeHtml(currentCampaignName)}</strong> campaign. Is this what your question is about?`;
          } else {
            return "What would you like to ask about?";
          }
        })(),
        title: "",
      },

      // From Case page: Check if different case is in same campaign
      "case-same-campaign-check": {
        choices: [
          {
            id: "yes-same-campaign",
            nextStep: "case-picker-same-campaign",
            text: "Yes, same campaign",
          },
          {
            id: "no-different-campaign",
            nextStep: "campaign-picker-then-case",
            text: "No, different campaign",
          },
        ],
        id: "case-same-campaign-check",
        question: (() => {
          const currentCampaignName = currentPageContext.value.campaign?.name || "this campaign";
          return `Okay — is the case you're asking about also part of the <strong>${escapeHtml(currentCampaignName)}</strong> campaign?`;
        })(),
        title: "",
      },

      // From Campaign page: Check if case is in same campaign
      "case-same-campaign-check-from-campaign": {
        choices: [
          {
            id: "yes-same-campaign",
            nextStep: "case-picker-same-campaign",
            text: "Yes, same campaign",
          },
          {
            id: "no-different-campaign",
            nextStep: "campaign-picker-then-case",
            text: "No, different campaign",
          },
        ],
        id: "case-same-campaign-check-from-campaign",
        question: (() => {
          const currentCampaignName = currentPageContext.value.campaign?.name || "this campaign";
          return `Okay — is the case you're asking about also part of the <strong>${escapeHtml(currentCampaignName)}</strong> campaign?`;
        })(),
        title: "",
      },

      // Case picker for same campaign
      "case-picker-same-campaign": {
        choices: [], // Will be populated by CasePicker component
        id: "case-picker-same-campaign",
        question:
          "Got it. Please select the case you want to ask about. You can search by case number or name.",
        title: "",
      },

      // Campaign picker from case page
      "campaign-picker-from-case": {
        choices: [], // Will be populated by CampaignPicker component
        id: "campaign-picker-from-case",
        question: "Sure — which campaign is your question about?",
        title: "",
      },

      // Campaign picker from campaign page
      "campaign-picker-from-campaign": {
        choices: [], // Will be populated by CampaignPicker component
        id: "campaign-picker-from-campaign",
        question: "Sure — which campaign is your question about?",
        title: "",
      },

      // Campaign picker then case
      "campaign-picker-then-case": {
        choices: [], // Will be populated by CampaignPicker component
        id: "campaign-picker-then-case",
        question: "Sure — which campaign is your question about?",
        title: "",
      },

      // Campaign picker for case (from firm)
      "campaign-picker-for-case": {
        choices: [], // Will be populated by CampaignPicker component
        id: "campaign-picker-for-case",
        question: "Sure — which campaign is your question about?",
        title: "",
      },

      // Campaign picker for campaign (from firm)
      "campaign-picker-for-campaign": {
        choices: [], // Will be populated by CampaignPicker component
        id: "campaign-picker-for-campaign",
        question: "Sure — which campaign is your question about?",
        title: "",
      },

      // After selecting campaign from case page
      "case-picker-after-campaign": {
        choices: [], // Will be populated by CasePicker component
        id: "case-picker-after-campaign",
        question: "Now choose the case from that campaign.",
        title: "",
      },

      // After selecting campaign from campaign page, ask about specific case
      "campaign-case-question": {
        choices: [
          {
            id: "yes-need-case",
            nextStep: "case-picker-from-campaign-selection",
            text: "Yes, I need to select a case",
          },
          {
            action: "start-conversation",
            contextType: "campaign",
            id: "no-overall-campaign",
            text: "No, it's about the overall campaign",
          },
        ],
        id: "campaign-case-question",
        question: "Got it. Is your question about a specific case in this campaign?",
        title: "",
      },

      // Case picker after campaign selection
      "case-picker-from-campaign-selection": {
        choices: [], // Will be populated by CasePicker component
        id: "case-picker-from-campaign-selection",
        question: "Now choose the case from that campaign.",
        title: "",
      },
    };
  });

  // Get current step
  const currentStep = computed(() => {
    return steps.value[selection.value.step] || steps.value.initial;
  });

  // Navigate to a step
  function goToStep(stepId: string) {
    // Add current step to history for back navigation
    if (selection.value.step !== stepId) {
      stepHistory.value.push(selection.value.step);
    }

    selection.value.step = stepId;
  }

  // Go back to previous step
  function goBack() {
    const currentStepData = currentStep.value;

    if (currentStepData.backStep) {
      selection.value.step = currentStepData.backStep;
      return;
    }

    if (stepHistory.value.length > 0) {
      const previousStep = stepHistory.value.pop();
      if (previousStep) {
        selection.value.step = previousStep;
      }
    }
  }

  // Handle choice selection
  function selectChoice(choice: ContextChoice) {
    // Handle immediate actions
    if (choice.action === "start-conversation") {
      selection.value.type = choice.contextType || "firm";

      // Set context data based on type
      if (choice.contextType === "case" && currentPageContext.value.type === "case") {
        // For case context, set case ID, campaign ID, and case name from current page
        selection.value.caseId = currentPageContext.value.id;
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        selection.value.campaignId = (currentPageContext.value as any).campaignId;

        // Build case display name from campaign store data (same source as the question text)
        const orgDetails = currentOrgCampaignDetails.value;
        if (orgDetails?.cases) {
          const currentCase = orgDetails.cases.find(
            (c) => c.id?.toString() === currentPageContext.value.id?.toString()
          );
          if (currentCase) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { caseNumber, firstName, lastName } = currentCase as any;
            const name = [firstName, lastName].filter(Boolean).join(" ");
            const num = caseNumber || currentPageContext.value.id;
            selection.value.caseName = name ? `${name} (#${num})` : `Case #${num}`;
          }
        }
      } else if (
        choice.contextType === "campaign" &&
        currentPageContext.value.type === "campaign" &&
        !selection.value.campaignId // Only use current page if no campaign has been selected
      ) {
        selection.value.campaignId = currentPageContext.value.id;
      }

      return "start-conversation";
    }

    // Handle selection actions
    if (choice.action === "select-case") {
      selection.value.type = "case";
      selection.value.caseId = choice.id;
      return "start-conversation";
    }

    if (choice.action === "select-campaign") {
      selection.value.type = "campaign";
      selection.value.campaignId = choice.id;
      return "start-conversation";
    }

    // Navigate to next step
    if (choice.nextStep) {
      goToStep(choice.nextStep);
      return "continue";
    }

    return "continue";
  }

  // Reset selection state
  function reset() {
    selection.value = {
      data: {},
      step: "initial",
      type: null,
    };
    stepHistory.value = [];
  }

  // Get cases for picker (with optional campaign filter)
  function getCasesForPicker(): ContextChoice[] {
    // For now, return placeholder data since cases need to be loaded asynchronously
    // In a real implementation, you'd need to load cases first or use a reactive approach
    const placeholderCases: ContextChoice[] = [
      {
        action: "select-case" as const,
        description: "Please wait while we load available cases",
        id: "case-1",
        text: "Loading cases...",
      },
    ];

    return placeholderCases;
  }

  // Get campaigns for picker. Only include campaigns the user's org owns —
  // campaign-level chat is owner-only at the route, so offering campaigns
  // the user has only view-shared access to creates a 403 dead-end. Picking
  // such a campaign should instead funnel through the case-picker for the
  // user's view-filtered cases (currently a separate flow).
  function getCampaignsForPicker(): ContextChoice[] {
    return allCampaigns.value
      .filter((campaign) => authStore.isCampaignOwner(campaign.createdByOrgId))
      .map((campaign) => ({
        action: "select-campaign" as const,
        id: campaign.id,
        text: campaign.name,
      }));
  }

  // Get choices for current step (with dynamic population)
  const currentChoices = computed(() => {
    const step = currentStep.value;

    // All case picker steps
    if (step.id.includes("case-picker")) {
      //   const campaignId =
      //     currentPageContext.value.type === "campaign" ? currentPageContext.value.id : undefined;

      return getCasesForPicker();
    }

    // All campaign picker steps
    if (step.id.includes("campaign-picker")) {
      return getCampaignsForPicker();
    }

    return step.choices;
  });

  return {
    canGoBack: computed(() => {
      return !!currentStep.value.backStep || stepHistory.value.length > 0;
    }),
    currentChoices,
    currentPageContext,
    currentStep,
    goBack,
    goToStep,
    reset,
    selectChoice,
    selection: computed(() => selection.value),
  };
}
